import React, { useEffect, useMemo, useState } from 'react';
import { toolbarButtons } from '../PacsToolbar/ToolbarBtn';

const HELP_SECTIONS = toolbarButtons.filter(section => section.title !== 'Mobile View');

const WORKSPACE_SECTIONS = [
  {
    title: 'Left Panel',
    description: 'Use the left panel to browse the patient series and study workspace.',
    details: [
      'Studies: View the patient study list, series, thumbnails, and available image sets.',
      'History: Review the clinical history and previous actions for the patient.',
      // 'Chest AI: Open the AI findings workspace when AI results are available for the CHEST study.',
      'Use the panel handle to resize the panel, or its collapse control to give the viewport more space.',
    ],
  },
  {
    title: 'Studies & History',
    description: "Find the right examination and compare the patient's imaging timeline.",
    details: [
      // 'Choose a study to load its series into the viewer.',
      'Open a series to inspect its image thumbnails and select the required image stack.',
      'Use History to locate previous examinations for comparison with the current study.',
      'Drag a series or study into a viewport when you need a specific arrangement.',
    ],
  },
  // {
  //   title: 'Chest AI',
  //   description: 'Review computer-assisted findings alongside the diagnostic images.',
  //   details: [
  //     'Shows AI AI Findings Bounding box and results of pathalogy for the selected study.',
  //     'Review the available findings and use them as an aid while interpreting the images.',
  //     'Select a finding to locate the related image or region when that link is provided.',
  //     'AI output is decision support and should be interpreted together with the images and clinical context.',
  //   ],
  // },
  {
    title: 'Right Panel',
    description: 'Use the right panel for Reporting, measurements, annotations, and study-level actions.',
    details: [
      'Reporting: Create a structured report for the active study, including measurements and annotations with help of advanced editor.',
      'Measurements: See all measurements and annotations created in the active study.',
      'Select a measurement to highlight it and jump to its location in the image.',
      'Edit an annotation from the measurement row when you need to update its label.',
      'Resize or collapse the panel when reviewing images or working with a larger viewport.',
    ],
  },
  {
    title: 'Measurements',
    description: 'Create, review, edit, and manage measurements drawn on the viewport.',
    details: [
      'Choose a measurement tool from the toolbar, then draw on the active viewport.',
      'The measurement table lists the measurements and annotations for the active study.',
      'Click a row to select it and jump to its image location; edit labels when needed.',
      'Use the table actions to clear measurements, export a CSV, or create a structured report.',
    ],
  },
  {
    title: 'Measurement Table & Report',
    description: 'Manage measurement data and create a report from your findings.',
    details: [
      'Export CSV to download the current measurements for review or external analysis.',
      'Create Report stores tracked measurements as a structured report for the study.',
      'Only measurements belonging to the active study are included in the report.',
      'Clear Measurements removes the displayed measurements after confirmation or action.',
    ],
  },
  {
    title: 'Viewport Workspace',
    description: 'The central viewport is where images are displayed and tools are applied.',
    details: [
      'Click a viewport to make it active before using a tool or keyboard shortcut.',
      'Use the layout controls to switch between one-up, multi-viewport, MPR, or 3D views.',
      'Scroll through image stacks, pan, zoom, rotate, and adjust window level as needed.',
      'The toolbar and viewport overlays show the available state and image information.',
    ],
  },
];

const GUIDE_SECTIONS = WORKSPACE_SECTIONS.map(section => ({
  ...section,
  type: 'guide',
}));

const TOOL_GUIDE_SECTIONS = HELP_SECTIONS.map(section => ({
  ...section,
  type: 'tools',
}));

const ALL_SECTIONS = [...GUIDE_SECTIONS, ...TOOL_GUIDE_SECTIONS];

const ViewerHelpModal = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState(GUIDE_SECTIONS[0]?.title || 'Left Panel');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const visibleSections = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return ALL_SECTIONS.map(section => ({
      ...section,
      icons: section.icons?.filter(tool => {
        if (!normalizedSearch) {
          return true;
        }

        return `${tool.label} ${tool.description}`.toLowerCase().includes(normalizedSearch);
      }),
      details: section.details?.filter(detail =>
        `${section.title} ${section.description} ${detail}`.toLowerCase().includes(normalizedSearch)
      ),
    })).filter(section => {
      if (!normalizedSearch) {
        return true;
      }

      return section.type === 'tools' ? section.icons.length > 0 : section.details.length > 0;
    });
  }, [searchTerm]);

  const activeSectionData = visibleSections.find(section => section.title === activeSection);
  const displayedSection = activeSectionData || visibleSections[0];

  useEffect(() => {
    if (displayedSection && displayedSection.title !== activeSection) {
      setActiveSection(displayedSection.title);
    }
  }, [activeSection, displayedSection]);

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-[#020617]/75 p-3 backdrop-blur-sm sm:p-6"
      onMouseDown={event => event.target === event.currentTarget && onClose()}
    >
      <div className="flex h-[min(760px,calc(100vh-24px))] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-sky-400/40 bg-[#111827] shadow-[0_18px_70px_rgba(2,132,199,0.28)]">
        <header className="flex items-start justify-between border-b border-slate-700 bg-[#0b1220] px-5 py-4 sm:px-7">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">
              Medimaze Viewer
            </p>
            <h2 className="text-xl font-semibold text-white sm:text-2xl">Viewer Help</h2>
            <p className="mt-1 text-sm text-slate-400">
              Explore the tools available in your viewer workspace.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close help dialog"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-600 text-xl text-slate-300 transition hover:border-sky-400 hover:bg-slate-800 hover:text-white"
          >
            ×
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <aside className="border-b border-slate-700 bg-[#0d1626] p-3 md:w-56 md:border-b-0 md:border-r md:p-4">
            <label className="mb-3 block">
              <span className="sr-only">Search viewer tools</span>
              <input
                type="search"
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Search tools"
                className="h-9 w-full rounded-md border border-slate-600 bg-[#111d31] px-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-sky-400"
              />
            </label>
            <nav className="flex gap-2 overflow-x-auto pb-1 md:block md:space-y-1">
              {ALL_SECTIONS.map(section => {
                const isVisible = visibleSections.some(item => item.title === section.title);
                const isActive = displayedSection?.title === section.title;

                return (
                  <button
                    key={section.title}
                    type="button"
                    disabled={!isVisible}
                    onClick={() => setActiveSection(section.title)}
                    className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition md:block md:w-full ${
                      isActive
                        ? 'bg-sky-500/15 font-semibold text-sky-200 ring-1 ring-sky-400/40'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                    } ${!isVisible ? 'cursor-not-allowed opacity-40' : ''}`}
                  >
                    {section.title}
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {displayedSection ? (
              <>
                <div className="mb-5 flex items-end justify-between gap-3 border-b border-slate-700 pb-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-400">
                      {displayedSection.type === 'guide' ? 'Workspace guide' : 'Tool guide'}
                    </p>
                    <h3 className="mt-1 text-xl font-semibold text-white">
                      {displayedSection.title}
                    </h3>
                  </div>
                  <span className="rounded-full border border-slate-600 px-2.5 py-1 text-xs text-slate-400">
                    {displayedSection.type === 'guide'
                      ? `${displayedSection.details.length} topics`
                      : `${displayedSection.icons.length} tools`}
                  </span>
                </div>
                {displayedSection.type === 'guide' ? (
                  <article className="rounded-lg border border-slate-700 bg-[#172235] p-5">
                    <p className="mb-5 text-sm leading-6 text-slate-300">
                      {displayedSection.description}
                    </p>
                    <div className="space-y-3">
                      {displayedSection.details.map(detail => (
                        <div
                          key={detail}
                          className="flex gap-3 text-sm leading-6 text-slate-300"
                        >
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
                          <span>{detail}</span>
                        </div>
                      ))}
                    </div>
                  </article>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {displayedSection.icons.map(tool => {
                      const ToolIcon = tool.icon;

                      return (
                        <article
                          key={`${displayedSection.title}-${tool.id}`}
                          className="rounded-lg border border-slate-700 bg-[#172235] p-4 transition hover:border-sky-400/60 hover:bg-[#1b2b43]"
                        >
                          <div className="mb-3 flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-sky-400/10 text-sky-300">
                              <ToolIcon className="h-5 w-5" />
                            </div>
                            <h4 className="text-sm font-semibold text-white">{tool.label}</h4>
                          </div>
                          <p className="text-sm leading-5 text-slate-400">{tool.description}</p>
                        </article>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                No tools match your search.
              </div>
            )}
          </main>
        </div>

        <footer className="border-t border-slate-700 bg-[#0b1220] px-5 py-3 text-xs text-slate-400 sm:px-7">
          <span className="font-semibold text-sky-300">Quick tip:</span> Select a tool from the
          toolbar, then click on it to use inside the viewport . Press{' '}
          <kbd className="rounded border border-slate-600 bg-slate-800 px-1.5 py-0.5 text-slate-200">
            Esc
          </kbd>{' '}
          to close this guide.
        </footer>
      </div>
    </div>
  );
};

export default ViewerHelpModal;
